import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { translate } from 'components/Translation';
import CardBody from 'components/Card/CardBody';
import style from 'assets/jss/material-dashboard-pro-react/views/contactUs/registeringComplaintStyle';
import { HeadingSBold } from 'components/Typography/Typography';
import { makeStyles } from '@material-ui/core';
import { ContactService } from 'services/contact/contact';
import { getLanguage } from 'utils/language/language';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import SimpleTabs from 'components/SimpleTabs/SimpleTabs';
import SingleTab from './subcomponents/SingleTab';
import { SingleTroubleTicketingCategory } from 'utils/api/models/troubleTicketing/singleCategory';
import { CreateOsmInteractionData } from 'utils/api/payloads/microApi';
import Success from './subcomponents/Success';
import Failure from './subcomponents/Failure';
import { AccountsService } from 'services/accounts/accounts';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { Source } from 'utils/const/source';
import invalidateCache from 'utils/api/invalidateCache';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { useSelector } from 'react-redux';
import { upIdSelector } from 'redux/modules/session/selectors';

// @ts-ignore
const useStyles = makeStyles(style);

const RegisteringComplaint = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [sending, setSending] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<number>(0);
	const [success, setSuccess] = useState<boolean>(false);
	const [failure, setFailure] = useState<boolean>(false);
	const [ticketNumber, setTicketNumber] = useState<string>('');
	const [corpAccounts, setCorpAccounts] = useState<SingleCorpAccount[]>([]);
	const [troubleTicketingCategories, setTroubleTicketingCategories] = useState<SingleTroubleTicketingCategory[]>([]);
	const classes = useStyles();
	const lang = getLanguage();
	const accId = useSelector(upIdSelector);

	const sendData = (data: CreateOsmInteractionData) => {
		setSending(true);

		const jsonData = new Blob(
			[
				JSON.stringify({
					category: data.category,
					subcategory: data.subcategory,
					type: data.type,
					fullName: data.fullName,
					contactInfo: data.contactInfo,
					accountCode: data.accountCode,
					assignmentGroup: data.assignmentGroup,
					contactNumber: data.contactNumber,
					descriptions: [data.message],
					notification: {
						id: data.customerEmail,
						type: 'E-mail',
					},
					source: Source.SELFCARE,
				}),
			],
			{
				type: 'application/json',
			}
		);
		const newFormData = new FormData();
		newFormData.append('jsonData', jsonData);
		for (const formDataPair of data.attachments.entries()) {
			newFormData.append('attachments', formDataPair[1]);
		}
		const name = troubleTicketingCategories[activeTab].tab;
		const actionName = 'register_complaint';
		setDataLayer(actionName, getLanguage(), null, accId, null, {
			staus: 'send',
			ticket_type: name,
		});
		ContactService.createOsmInteraction(newFormData)
			.pipe(finalize(() => setSending(false)))
			.subscribe(
				({ response }) => {
					setDataLayer(actionName, getLanguage(), null, accId, null, {
						staus: 'success',
						ticket_type: name,
					});
					setTicketNumber(response?.externalId);
					setSuccess(true);
					invalidateCache(['getTickets']);
				},
				() => {
					setDataLayer(actionName, getLanguage(), null, accId, null, {
						staus: 'failed',
						ticket_type: name,
					});
					setFailure(true);
				}
			);
	};

	const newMessageClicked = () => {
		setSuccess(false);
		setTicketNumber('');
	};

	useEffect(() => {
		setLoading(true);
		ContactService.getTopics()
			.pipe(finalize(() => setLoading(false)))
			.subscribe(({ categories }) => setTroubleTicketingCategories(categories));
	}, [lang]);

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setCorpAccounts(accounts);
		});
	}, []);

	useEffect(() => {
		if (troubleTicketingCategories.length) {
			const name = troubleTicketingCategories[activeTab].tab;
			setDataLayer('support_ticket_type', getLanguage(), null, accId, null, {
				link_name: `Tab Clicked:${name}`,
			});
		}
	}, [activeTab, troubleTicketingCategories]);

	return (
		<>
			<ConditionalRender
				show={loading || sending}
				onTrue={() => (
					<div className={classes.loaderWrapper}>
						<CustomCircularProgress loading text={translate(sending ? 'contact.sending' : 'loading')} />
					</div>
				)}
			/>
			<CardBody className={classNames(classes.cardBody, { [classes.responseCardBody]: success || failure })}>
				<ConditionalRender
					show={success}
					onTrue={() => <Success onClose={newMessageClicked} ticketNumber={ticketNumber} />}
				/>
				<ConditionalRender show={failure} onTrue={() => <Failure onClose={() => setFailure(false)} />} />
				<ConditionalRender
					show={!success && !failure}
					onTrue={() => (
						<>
							<div className={classes.cardBodyContentWrapper}>
								<HeadingSBold disableMargin>{translate('contact.title')}</HeadingSBold>
							</div>
							<ConditionalRender
								show={!loading && troubleTicketingCategories.length > 0}
								onTrue={() => (
									<SimpleTabs
										tabs={troubleTicketingCategories.map((singleTabCategory, index) => ({
											name: singleTabCategory.tab,
											content: (
												<SingleTab
													key={index}
													category={singleTabCategory}
													sendData={sendData}
													corpAccounts={corpAccounts}
												/>
											),
										}))}
										activeTab={activeTab}
										setActiveTab={setActiveTab}
									/>
								)}
							/>
						</>
					)}
				/>
			</CardBody>
		</>
	);
};

export default RegisteringComplaint;
